import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from './hooks/useAuth'
import { ErrorBoundary } from './components/ErrorBoundary'
import AdminRoute from './components/AdminRoute'
import Login from './pages/Login'
import ExternalRedirect from './components/ExternalRedirect'
import Dashboard from './pages/Dashboard'
import Requests from './pages/Requests'
import Users from './pages/admin/Users'
import Analytics from './pages/admin/Analytics'
import UserAnalytics from './pages/admin/UserAnalytics'

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-noir-primary">
        <div className="text-center">
          <div
            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-accent-cyan border-r-transparent motion-reduce:animate-[spin_1.5s_linear_infinite]"
            role="status"
            aria-label="Loading"
          >
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-4 text-sm text-text-secondary">Checking authentication...</p>
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

export default function App() {
  const { isAuthenticated, isLoading } = useAuth()

  return (
    <ErrorBoundary>
      <Routes>
        <Route
          path="/login"
          element={
            isLoading ? (
              <div className="min-h-screen flex items-center justify-center bg-noir-primary">
                <div
                  className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-accent-cyan border-r-transparent"
                  role="status"
                  aria-label="Loading"
                >
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : isAuthenticated ? (
              <Navigate to="/" replace />
            ) : (
              <Login />
            )
          }
        />
        <Route path="/start" element={<ExternalRedirect to="https://codevet.dev/start" />} />
        <Route path="/pricing" element={<ExternalRedirect to="https://codevet.dev/pricing" />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/requests"
          element={
            <ProtectedRoute>
              <Requests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <Users />
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <Analytics />
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/analytics/:userId"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <UserAnalytics />
              </AdminRoute>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  )
}
